/**
 * EtStartFlow — de controleschermen vóór de eerste vraag (Facet-blauwdruk §1).
 *
 * Leerlingen doorlopen op het echte digitale examen eerst een vaste reeks
 * stappen voordat de toets begint. Die reeks bouwen we hier na in
 * TestWisely-stijl, met eigen teksten:
 *   1. gegevenscontrole   2. geluidscontrole (testtoon + volume + goed/niet goed)
 *   3. stemkeuze (voorlezen, Web Speech API)   4. toetsoverzicht   5. uitleg toolbox
 *
 * Zelfstandig component: props { examName, deelnemer, toetsInfo, onKlaar }.
 * Geluid: een korte, vriendelijk klinkende toonreeks via WebAudio — geen
 * extern bestand nodig en werkt overal.
 */
import React, { useState, useRef, useEffect } from 'react';
import { QvHeader, QvButton, QvIcon } from '../../pages/QuestionView';
import './EtStartFlow.css';

const STAPPEN = ['Controle', 'Geluid', 'Stem', 'Overzicht', 'Uitleg'];

export default function EtStartFlow({ examName, deelnemer, toetsInfo, onKlaar,
  serverStemmen = [], onProefServer = null }) {
  const [stap, setStap] = useState(0);
  const kopRef = useRef(null);
  const [volume, setVolume] = useState(0.5);
  const [geluidOk, setGeluidOk] = useState(null);      // null | true | false
  const [stemmen, setStemmen] = useState([]);
  const [stemIdx, setStemIdx] = useState(0);
  const [snelheid, setSnelheid] = useState(1);   // CvTE-eis: tempo regelbaar
  const [proefBezig, setProefBezig] = useState(false);
  const [speelt, setSpeelt] = useState(false);
  const audioRef = useRef(null);

  // Review-fix #14: bij een stap-wissel kan de Volgende-knop disabled raken
  // onder de focus (die valt dan op body). Focus daarom de kop van de stap.
  useEffect(() => { if (kopRef.current) kopRef.current.focus(); }, [stap]);

  // NL-stemmen ophalen (Web Speech API laadt ze soms asynchroon)
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const laad = () => {
      const alle = window.speechSynthesis.getVoices();
      const nl = alle.filter((v) => v.lang && v.lang.toLowerCase().startsWith('nl'));
      // Kwaliteit eerst: netwerk-stemmen (Chrome 'Google Nederlands') en
      // 'Enhanced/Premium'-stemmen (Apple) klinken veel natuurlijker dan de
      // kale systeemstemmen. Die krijgen voorrang in de keuzelijst.
      const score = (v) => (v.localService === false ? 2 : 0)
        + (/enhanced|premium|natural|google/i.test(v.name) ? 1 : 0);
      nl.sort((a, b) => score(b) - score(a));
      setStemmen(nl.length ? nl : alle.slice(0, 4));
    };
    laad();
    window.speechSynthesis.onvoiceschanged = laad;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      window.speechSynthesis.cancel();   // review-fix #12: niet doorpraten na unmount
    };
  }, []);

  // Review-fix #12: stem stopt zodra de leerling stap 2 verlaat.
  useEffect(() => {
    if (stap !== 2 && window.speechSynthesis) window.speechSynthesis.cancel();
  }, [stap]);

  // ── testtoon: kort vriendelijk deuntje via WebAudio ──
  const speelToon = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const noten = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
      setSpeelt(true);
      noten.forEach((f, k) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine'; osc.frequency.value = f;
        gain.gain.value = 0;
        osc.connect(gain); gain.connect(ctx.destination);
        const t = ctx.currentTime + k * 0.28;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.28 * volume, t + 0.04);
        gain.gain.linearRampToValueAtTime(0, t + 0.26);
        osc.start(t); osc.stop(t + 0.3);
      });
      setTimeout(() => setSpeelt(false), noten.length * 280 + 150);
    } catch (e) { setSpeelt(false); }
  };

  // Serverstemmen (zelfgehoste Piper) staan vóór de browserstemmen: ze klinken
  // natuurlijker en de audio komt van de eigen server.
  const alleStemmen = [
    ...serverStemmen.map((st) => ({ soort: 'server', id: st.id, naam: st.naam })),
    ...stemmen.map((v) => ({ soort: 'browser', voice: v, naam: v.name.split(' ')[0] })),
  ];

  const probeerStem = (idx) => {
    setStemIdx(idx);
    const st = alleStemmen[idx];
    if (!st) return;
    if (st.soort === 'server') {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (onProefServer) {
        setProefBezig(true);
        Promise.resolve(onProefServer(st.id, volume, snelheid))
          .finally(() => setProefBezig(false));
      }
      return;
    }
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance('Hoi! Zo klink ik. Kies mij als je dit prettig vindt.');
    u.voice = st.voice;
    u.volume = volume;
    u.rate = snelheid;
    window.speechSynthesis.speak(u);
  };

  const verder = () => {
    if (stap === STAPPEN.length - 1) {
      const st = alleStemmen[stemIdx] || null;
      onKlaar({
        volume,
        snelheid,
        stem: st && st.soort === 'browser' ? st.voice : null,
        serverStem: st && st.soort === 'server' ? st.id : null,
      });
    }
    else setStap((s) => s + 1);
  };
  // Review-fix #11: wie eerlijk 'ik hoor niets' kiest mag óók verder — de
  // controle is dat er gekozen is, niet dat het antwoord 'goed' luidt.
  const magVerder = stap !== 1 || geluidOk !== null;

  return (
    <div className="tw-qv-root toets-maken-container">
      <QvHeader examName={`${examName} · ${STAPPEN[stap]}`} studentName={deelnemer}
        current={0} total={toetsInfo.aantalVragen} seconds={null} />
      <div className="ets-stappen" aria-label="Voortgang startcontrole">
        {STAPPEN.map((naam, k) => (
          <span key={naam} className={`ets-stip ${k === stap ? 'nu' : k < stap ? 'klaar' : ''}`}>{naam}</span>
        ))}
      </div>

      <main className="ets-main">
        <div className="ets-card">

          {stap === 0 && (
            <>
              <h2 ref={kopRef} tabIndex={-1}>Kloppen je gegevens?</h2>
              <p>Kijk goed of dit klopt. Klopt er iets niet? Zeg het dan tegen je begeleider.</p>
              <dl className="ets-gegevens">
                <div><dt>Training</dt><dd>{examName}</dd></div>
                <div><dt>Naam</dt><dd>{deelnemer}</dd></div>
                <div><dt>Aantal vragen</dt><dd>{toetsInfo.aantalVragen}</dd></div>
              </dl>
            </>
          )}

          {stap === 1 && (
            <>
              <h2 ref={kopRef} tabIndex={-1}>Werkt je geluid?</h2>
              <p>Klik op de knop en luister. Te zacht of te hard? Schuif de balk. Hoor je niets? Kies dan "Ik hoor niets".</p>
              <div className="ets-geluid-rij">
                <QvButton variant="primary" onClick={speelToon} ariaLabel="Speel het testgeluid af">
                  <QvIcon name={speelt ? 'pause' : 'play'} size={16} stroke={2} />
                  {speelt ? 'Speelt…' : 'Geluid afspelen'}
                </QvButton>
                <label className="ets-volume">
                  <span className="ets-sr">Volume</span>
                  <QvIcon name="volume" size={18} stroke={2} />
                  <input type="range" min="0" max="1" step="0.05" value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))} aria-label="Volume instellen" />
                </label>
              </div>
              <fieldset className="ets-keuze">
                <legend className="ets-sr">Is het geluid goed?</legend>
                <label className={geluidOk === true ? 'gekozen' : ''}>
                  <input type="radio" name="geluid" checked={geluidOk === true} onChange={() => setGeluidOk(true)} />
                  Het geluid is goed
                </label>
                <label className={geluidOk === false ? 'gekozen' : ''}>
                  <input type="radio" name="geluid" checked={geluidOk === false} onChange={() => setGeluidOk(false)} />
                  Ik hoor niets / het is niet goed
                </label>
              </fieldset>
              {geluidOk === false && (
                <p className="ets-hint">Controleer of je geluid aanstaat en niet op stil staat. Lukt het niet? Vraag je begeleider om hulp. Je kunt ook zonder geluid gewoon oefenen: klik op Volgende.</p>
              )}
            </>
          )}

          {stap === 2 && (
            <>
              <h2 ref={kopRef} tabIndex={-1}>Kies een stem</h2>
              <p>Sommige vragen kunnen worden voorgelezen. Klik op een stem om die te horen. Je kunt dit later altijd veranderen.</p>
              {alleStemmen.length === 0 ? (
                <p className="ets-hint">Er zijn op dit apparaat geen voorleesstemmen gevonden. Je kunt gewoon verdergaan.</p>
              ) : (
                <div className="ets-stemmen" role="radiogroup" aria-label="Voorleesstem kiezen">
                  {alleStemmen.slice(0, 6).map((st, idx) => (
                    <button key={`${st.soort}-${st.naam}-${idx}`} type="button"
                      className={`ets-stem ${idx === stemIdx ? 'gekozen' : ''} ${idx === stemIdx && proefBezig ? 'bezig' : ''}`}
                      aria-pressed={idx === stemIdx}
                      onClick={() => probeerStem(idx)}>
                      <QvIcon name="volume" size={16} stroke={2} />
                      {st.naam}{idx === stemIdx && proefBezig ? '…' : ''}
                    </button>
                  ))}
                </div>
              )}
              {alleStemmen.length > 0 && (
                <div className="ets-tempo">
                  <label htmlFor="ets-tempo-schuif">Voorleessnelheid</label>
                  <input id="ets-tempo-schuif" type="range" min="0.6" max="1.4" step="0.1"
                    value={snelheid}
                    onChange={(e) => setSnelheid(Number(e.target.value))}
                    aria-valuetext={`${snelheid.toFixed(1)} keer de normale snelheid`} />
                  <span className="ets-tempo-waarde">
                    {snelheid < 0.9 ? 'rustig' : snelheid > 1.1 ? 'snel' : 'normaal'}
                  </span>
                </div>
              )}
            </>
          )}

          {stap === 3 && (
            <>
              <h2 ref={kopRef} tabIndex={-1}>Over deze training</h2>
              <dl className="ets-gegevens">
                <div><dt>Naam</dt><dd>{examName}</dd></div>
                <div><dt>Aantal vragen</dt><dd>{toetsInfo.aantalVragen}</dd></div>
                <div><dt>Te behalen punten</dt><dd>{toetsInfo.maxPunten}</dd></div>
                <div><dt>Tijd</dt><dd>{toetsInfo.duurMinuten ? `${toetsInfo.duurMinuten} minuten` : 'Geen tijdslimiet — dit is oefenen'}</dd></div>
              </dl>
              <p>Bovenin het scherm vind je bij sommige vragen hulpmiddelen, zoals de rekenmachine.</p>
            </>
          )}

          {stap === 4 && (
            <>
              <h2 ref={kopRef} tabIndex={-1}>Zo werkt het antwoordveld</h2>
              <p>Bij rekenvragen laat je zien hoe je aan je antwoord komt. Daarvoor heb je deze knoppen:</p>
              <ul className="ets-uitleg">
                <li><b>Breuk, macht en wortel</b> — klik op de knop en het symbool komt in je antwoord. Typ daarna de getallen erin.</li>
                <li><b>Tabel</b> — zet een tabel in je antwoord. Sta je in de tabel, dan zie je knoppen om rijen en kolommen toe te voegen of weg te halen.</li>
                <li><b>Rekenmachine</b> — reken iets uit en klik op "Zet in antwoord". Je berekening komt dan vanzelf in je antwoord te staan.</li>
                <li><b>Voorlezen</b> — klik op het luidsprekertje bovenin, of druk op <b>Ctrl+K</b>, om de opgave en de vraag te laten voorlezen.</li>
              </ul>
              <p>Klik op "Beginnen" om te starten. Succes!</p>
            </>
          )}

        </div>
      </main>

      <footer className="etq-footer">
        <QvButton variant="secondary" onClick={() => setStap((s) => Math.max(0, s - 1))}
          disabled={stap === 0} ariaLabel="Vorige stap">
          <QvIcon name="arrow-left" size={16} stroke={2} /> Vorige
        </QvButton>
        <span />
        <QvButton variant={stap === STAPPEN.length - 1 ? 'submit' : 'primary'}
          onClick={verder} disabled={!magVerder}
          ariaLabel={stap === STAPPEN.length - 1 ? 'Begin met oefenen' : 'Volgende stap'}>
          {stap === STAPPEN.length - 1 ? 'Beginnen' : 'Volgende'} <QvIcon name="arrow-right" size={16} stroke={2} />
        </QvButton>
      </footer>
    </div>
  );
}
